import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { addToCheckoutCart, initBedbankSession, initialiseCheckout } from 'actions/CheckoutActions'
import * as Analytics from 'analytics/analytics'
import { addToCartGTM } from 'analytics/eventDefinitions'
import { addToCart, propertyRateImpressionEventWithContext } from 'analytics/snowplow/events'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import ProcessingModal from 'checkout/Components/Booking/Modals/ProcessingModal'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { generatePassengersFromOccupants } from 'checkout/lib/utils/flights/passenger'
import AccommodationPackageInclusionsList from 'components/AccommodationKit/AccommodationPackageInclusionsList'
import AccommodationPackageTile from 'components/AccommodationKit/AccommodationPackageTile'
import AccommodationPackageTileContentArea from 'components/AccommodationKit/AccommodationPackageTileContentArea'
import AccommodationPackageTileDetails from 'components/AccommodationKit/AccommodationPackageTileDetails'
import AccommodationPackageTilePricingArea from 'components/AccommodationKit/AccommodationPackageTilePricingArea'
import AccommodationPackageTilePricingBlock from 'components/AccommodationKit/AccommodationPackageTilePricingBlock'
import AccommodationPackageTilePricingBlocks from 'components/AccommodationKit/AccommodationPackageTilePricingBlocks'
import AccountAccessModal, { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PayLaterPill from 'components/Common/PayLaterPill'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { FlightViewTypes } from 'constants/flight'
import ModalContext from 'contexts/ModalContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useAppDispatch } from 'hooks/reduxHooks'
import useBedbankDiscount from 'hooks/useBedbankDiscount'
import {
  generateBedbankPropertyAccommodationCheckoutItems,
  generateLpcPropertyAccommodationCheckoutItems,
} from 'lib/checkout/accommodation/cart'
import { generateFlightCheckoutItem } from 'lib/checkout/flights/cart'
import { countOccupantsForFlights, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import {
  generateLoyaltyPointsCalculatorPropertyBundledFlightOptions,
  generateLoyaltyPointsCalculatorPropertyOptions,
} from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { getEnquirySubscriber } from 'luxPlus/selectors/checkout/order'
import { selectLoggedIn } from 'selectors/accountSelectors'
import PropertyAvailableRateOptionCtas from './PropertyAvailableRateOptionCtas'
import PropertyCancellationPolicy from './PropertyCancellationPolicy'
import PropertyUnavailableRateOptionCtas from './PropertyUnavailableRateOptionCtas'

interface Props {
  lpcOffer: App.HotelOffer;
  bedbankOffer: App.BedbankOffer | undefined;
  rate: App.PropertyRate;
  optionNumber: number; // TODO one-indexed
  showOptionLabel: boolean;
  originAirport: App.Airport | undefined;
  checkIn: string | undefined;
  checkOut: string | undefined;
  duration: number;
  rooms: Array<App.Occupants>;
  regionCode: string;
  currency: string;
  isLoggedIn: boolean;
  subscriber: App.OrderSubscriber | undefined;
  // TODO analytics
  roomListId: string;
  roomPosition: number; // TODO one-indexed
  rateListId: string;
}

function PropertyRateOption(props: Props) {
  const {
    lpcOffer,
    bedbankOffer,
    rate,
    optionNumber,
    showOptionLabel,
    originAirport,
    checkIn,
    checkOut,
    duration,
    rooms,
    regionCode,
    currency,
    isLoggedIn,
    subscriber,
    roomListId,
    roomPosition,
    rateListId,
  } = props

  const dispatch = useAppDispatch()
  const offerPageDispatch = useOfferPageDispatch()
  const modalElementContext = useModalElementContext()
  const showModal = useContext(ModalContext)
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const [ref, inView] = useInView({ triggerOnce: true })

  // TODO maybe this should just use bedbank as a fallback?
  const propertyTimeZone = (rate.type === 'bedbank' && bedbankOffer) ? bedbankOffer.property.timezone : lpcOffer.property.timezone
  // TODO `formattedShortDateByTimezone` includes a hack for string timestamps suffixed with `'T00:00:00.000Z'`, which we want to avoid
  const headlineCancellationPolicyStart = rate.headlineCancellationPolicyStart ? new Date(rate.headlineCancellationPolicyStart) : undefined
  const packageNumber = showOptionLabel ? optionNumber : undefined
  const rawBedbankDiscount = useBedbankDiscount(rate.total.discount, rate.luxPlusTotal?.discount ?? 0)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    return [
      generateLoyaltyPointsCalculatorPropertyOptions(rate),
      generateLoyaltyPointsCalculatorPropertyBundledFlightOptions(rate),
    ]
  }, [rate])

  useEffect(() => {
    const offer = rate.type === 'lpc' ? lpcOffer : bedbankOffer
    if (inView && offer && rooms && checkIn && checkOut) {
      Analytics.trackEvent(propertyRateImpressionEventWithContext({
        offer,
        occupancy: rooms,
        checkIn,
        checkOut,
        roomListId,
        roomPosition,
        rateListId,
        ratePosition: optionNumber,
        rate,
        currency,
      }))
    }
  }, [
    bedbankOffer,
    checkIn,
    checkOut,
    currency,
    inView,
    lpcOffer,
    optionNumber,
    rate,
    rateListId,
    roomListId,
    roomPosition,
    rooms,
  ])

  const onCtaClick = useCallback(async(shouldKeepBrowsing: boolean) => {
    if (!(checkIn && checkOut)) throw new Error('Attempted to perform cart action when dates aren\'t set')
    trackEvent('BOOK_NOW_PRESSED')

    // TODO we don't care about walled garden specifics
    if (!isLoggedIn) {
      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
      if (!result.loggedIn) {
        return
      }
    }

    const period = { checkIn, checkOut, duration }
    const accommodationItems: Array<App.Checkout.InstantBookingLEHotelItem | App.Checkout.BedbankHotelItem> = []
    let offer: App.HotelOffer | App.BedbankOffer
    if (rate.type === 'lpc') {
      offer = lpcOffer
      accommodationItems.push(...generateLpcPropertyAccommodationCheckoutItems(
        lpcOffer,
        rate,
        period,
        rooms,
      ))
    } else if (rate.type === 'bedbank') {
      if (!bedbankOffer) return
      offer = bedbankOffer

      let sessionId: string | undefined
      if (!shouldKeepBrowsing) {
        let closeProcessingModal: any
        showModal((resolve) => {
          closeProcessingModal = resolve
          return <ProcessingModal message="Please wait..." />
        })
        const session = await initBedbankSession(dispatch, {
          propertyId: bedbankOffer.id,
          roomTypeId: rate.bedbankRoomId,
          roomRateId: rate.bedbankRateId,
          rateGroupId: rate.rateGroupId,
          bedGroupId: rate.bedGroups[0].id,
          isFlightBundle: rate.isFlightBundle,
          csBooking: false, // TODO
          region: regionCode,
          rooms,
          checkIn,
          checkOut,
          price: rate.total.sellingPrice,
          propertyFees: rate.total.propertyFees,
          memberInclusive: rate.luxPlusTotal?.sellingPrice,
          subscriber,
          // TODO source?
        })
        closeProcessingModal()
        if (!('sessionId' in session)) return // TODO
        sessionId = session.sessionId
      }

      accommodationItems.push(...generateBedbankPropertyAccommodationCheckoutItems(
        bedbankOffer,
        sessionId,
        rate,
        period,
        rooms,
      ))
    } else {
      assertUnreachable(rate)
    }

    const flightItems: Array<App.Checkout.FlightItem> = []
    if (rate.includesFlights) {
      if (!(originAirport?.code && typeof rate.cheapestFlightPrice === 'number')) {
        throw new Error('Attempted to perform cart action on rate with flights when no flight pricing is available')
      }
      const flightOccupants = countOccupantsForFlights(rooms)
      flightItems.push(generateFlightCheckoutItem({
        originAirportCode: originAirport?.code,
        destinationAirportCode: lpcOffer.flightDestinationPort || bedbankOffer?.flights.airportCode,
        occupants: flightOccupants,
        passengers: generatePassengersFromOccupants(flightOccupants),
        fareType: 'return',
        viewType: FlightViewTypes.TWO_ONE_WAYS,
        bundledItemIds: accommodationItems.map(({ itemId }) => itemId),
        quotedFare: rate.cheapestFlightPrice / rooms.length,
      }))
    }

    const checkoutItems = [...accommodationItems, ...flightItems]

    offerPageDispatch({ type: OfferPageActions.closeFilter })
    modalElementContext?.resolve()

    if (flightItems.length) {
      dispatch(initialiseCheckout(checkoutItems, { mode: 'vertical-select' }))
    } else {
      dispatch(addToCheckoutCart(checkoutItems))
    }
    // TODO confirm price is what data team want
    fireEcommerceGTMEvent(addToCartGTM(offer, currency, rate.luxPlusTotal?.displayPrice ?? rate.total.displayPrice, sumUpOccupancies(rooms), 'hotel'))
    // TODO figure out why the other rate types don't fire this for flights
    // NB: This event doesn't appear to fire immediately; it seems to be waiting on
    // `checkoutStateIsReady()`.
    Analytics.trackEvent(addToCart({
      roomListId,
      roomPosition,
      rateListId,
      ratePosition: optionNumber,
      type: 'property',
      rate,
      currency,
    }))

    if (flightItems.length) {
      dispatch(navigateToCheckout({ skipSpoofedModal: true }))
      return
    }

    if (shouldKeepBrowsing) {
      showSnackbar('Your item has been added to the cart.', 'success')
      return
    }

    dispatch(navigateToCheckout())
  }, [
    bedbankOffer,
    checkIn,
    checkOut,
    currency,
    dispatch,
    duration,
    isLoggedIn,
    lpcOffer,
    modalElementContext,
    offerPageDispatch,
    optionNumber,
    originAirport?.code,
    rate,
    rateListId,
    regionCode,
    roomListId,
    roomPosition,
    rooms,
    showModal,
    subscriber,
    trackEvent,
  ])
  const onToggleFilter = useCallback((filter: 'airport' | 'checkin') => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter })
  }, [offerPageDispatch])
  const ctas = <>
    {(!rate.includesFlights || typeof rate.cheapestFlightPrice === 'number') && <PropertyAvailableRateOptionCtas
      includesFlights={rate.includesFlights}
      onCtaClick={onCtaClick}
    />}
    {rate.includesFlights && typeof rate.cheapestFlightPrice === 'string' && <PropertyUnavailableRateOptionCtas
      shouldChangeAirport={rate.cheapestFlightPrice === 'originSameAsDestination' || rate.cheapestFlightPrice === 'unavailable'}
      shouldChangeDepartureDate={rate.cheapestFlightPrice === 'departureTooEarly' || rate.cheapestFlightPrice === 'departureTooLate'}
      onToggleFilter={onToggleFilter}
    />}
  </>

  const cancellationPolicy = <PropertyCancellationPolicy
    propertyTimeZone={propertyTimeZone!}
    headlineCancellationPolicyType={rate.headlineCancellationPolicyType}
    headlineCancellationPolicyStart={headlineCancellationPolicyStart}
    fullyRefundableUntil={rate.fullyRefundableUntil}
    cancellationPolicies={rate.cancellationPolicies}
    currency={currency}
    shouldClarifyRefundIsHotelOnly={rate.includesFlights}
  />

  if (rate.type === 'lpc') {
    const lpcDiscount = rate.total.discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? rate.total.discount : 0
    const shouldShowValue = rate.total.valuedUpToPrice > rate.total.sellingPrice
    const showPriceDetails = !!(rate.total.taxesAndFees || rate.total.propertyFees)

    return (
      <AccommodationPackageTile data-testid={rate.lpcRateId}>
        <AccommodationPackageTileContentArea>
          <AccommodationPackageTileDetails
            packageNumber={packageNumber}
            rateName={rate.name}
          >
            <Group direction="vertical" gap={8} noDisplayWhenEmpty>
              <Group direction="vertical" gap={4} noDisplayWhenEmpty>
                {cancellationPolicy}
              </Group>
            </Group>
          </AccommodationPackageTileDetails>
          <div>
            <LargeContentToggle
              maxHeight={208}
              showMoreText="View more inclusions"
              showLessText="View less inclusions"
            >
              <VerticalSpacer gap={12}>
                {rate.luxPlusInclusions.length > 0 && <LuxPlusInclusions
                  luxPlusInclusions={rate.luxPlusInclusions}
                  accommodationOffer={lpcOffer}
                />}
                {rate.bonusInclusions.length > 0 && <AccommodationPackageInclusionsList title="bonus">
                  {rate.bonusInclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
                </AccommodationPackageInclusionsList>}
                {rate.inclusions.length > 0 && <VerticalSpacer gap={4}>
                  {rate.inclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
                </VerticalSpacer>}
              </VerticalSpacer>
            </LargeContentToggle>
          </div>
        </AccommodationPackageTileContentArea>
        <AccommodationPackageTilePricingArea>
          {(!rate.includesFlights || (typeof rate.cheapestFlightPrice === 'number' && rate.cheapestFlightPrice > 0)) && <Group direction="horizontal" gap={4} wrap="wrap">
            {lpcDiscount > 0 && <PriceRowValueDiscount
              type="estimate"
              discountPercentage={lpcDiscount}
            />}
          </Group>}
          <AccommodationPackageTilePricingBlocks>
            <AccommodationPackageTilePricingBlock
              title={undefined}
              callToAction={ctas}
            >
              {rate.includesFlights && rate.cheapestFlightPrice === 'loading' && <LoadingIndicator floating opaque />}
              {(!rate.includesFlights || (typeof rate.cheapestFlightPrice === 'number' && rate.cheapestFlightPrice > 0)) && <VerticalSpacer gap={12}>
                <div>
                  <Caption variant="medium">
                    <Group direction="vertical" gap={4}>
                      <div>
                        <PriceRowPriceCaption>
                          {rate.includesFlights && <LinePlaneDepartureIcon size="S" />} {pluralizeToString('night', duration)} {rate.includesFlights && '+ flights from'}
                        </PriceRowPriceCaption>
                        <LuxPlusPriceGuard
                          showMemberPrice={(rate.luxPlusTotal?.displayPrice ?? 0) > 0}
                          fallbackPrice={<PriceRowPrice
                            size="M"
                            price={rate.total.displayPrice}
                            saleUnit="total"
                          />}
                        >
                          <LuxPlusPriceStack
                            size="M"
                            price={rate.total.displayPrice}
                            memberPrice={rate.luxPlusTotal?.displayPrice ?? 0}
                            saleUnit="total"
                          />
                        </LuxPlusPriceGuard>
                        <div ref={ref}></div>
                        <PriceRowTaxesAndFees />
                        {shouldShowValue && <PriceRowValueDiscountWithCaption
                          size="M"
                          originalValue={rate.total.valuedUpToPrice}
                        />}
                      </div>
                      {rate.includedGuestsExceeded && <AdditionalGuestsPopup
                        complex={false}
                        description="+ Extra guest surcharge"
                        modalContent={rate.includedGuestsExceededPolicy}
                      />}
                      {/* NB: Don't pass `flightPrice` to `<OfferPriceDetailsRow/>`, as it's already baked in. */}
                      {showPriceDetails && <OfferPriceDetailsRow
                        trigger="price-row"
                        triggerSize="M"
                        offerPage
                        offer={lpcOffer}
                        duration={duration}
                        propertyFees={rate.total.propertyFees}
                        hotelPrice={rate.total.sellingPrice}
                        taxesAndFees={rate.total.taxesAndFees}
                        hotelMemberPrice={rate.luxPlusTotal?.sellingPrice}
                        showMemberPrice={(rate.luxPlusTotal?.displayPrice ?? 0) > 0}
                        rooms={rooms}
                        dueAtPropertyMessage={lpcOffer.property.taxesAndFeesContent ?? ''}
                        cta={<Group direction="vertical" tabletDirection="horizontal-reverse" horizontalAlign="stretch" tabletHorizontalAlign="end" gap={8}>
                          {ctas}
                        </Group>}
                      />}
                    </Group>
                  </Caption>
                </div>
                <LoyaltyPointsEarnWithBreakdownModal
                  calculationRequests={pointsEarnCalculationRequests}
                  calculationType={rate.includesFlights ? 'estimate' : 'exact'}
                />
              </VerticalSpacer>}
              {rate.includesFlights && (typeof rate.cheapestFlightPrice === 'string' || rate.cheapestFlightPrice === 0) && <VerticalSpacer gap={12}>
                {(rate.cheapestFlightPrice === 'originSameAsDestination' || rate.cheapestFlightPrice === 'unavailable') && <BodyText colour="neutral-two" variant="small">
                  No flights available from {originAirport?.name}. Please select another departing airport.
                </BodyText>}
                {rate.cheapestFlightPrice === 'departureTooEarly' && <BodyText colour="neutral-two" variant="small">
                  Flights cannot be booked online when check-in is within 24 hours.
                </BodyText>}
                {rate.cheapestFlightPrice === 'departureTooLate' && <BodyText colour="neutral-two" variant="small">
                  Flights can only be booked up to 11 months in advance.
                </BodyText>}
                {rate.cheapestFlightPrice === 0 && <BodyText colour="neutral-two" variant="small">
                  Add flights to the hotel package. Select from all major airlines at the next step.
                </BodyText>}
              </VerticalSpacer>}
            </AccommodationPackageTilePricingBlock>
          </AccommodationPackageTilePricingBlocks>
        </AccommodationPackageTilePricingArea>
      </AccommodationPackageTile>
    )
  }

  if (rate.type === 'bedbank' && bedbankOffer) {
    const bedbankDiscount = rawBedbankDiscount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? rawBedbankDiscount : 0
    // TODO note we'll completely ignore `bedbankOffer.displayPricingAsPerPerson`

    return (
      <AccommodationPackageTile>
        <AccommodationPackageTileContentArea>
          <AccommodationPackageTileDetails
            packageNumber={packageNumber}
          >
            <Group direction="vertical" gap={8} noDisplayWhenEmpty>
              {cancellationPolicy}
            </Group>
          </AccommodationPackageTileDetails>
          <div>
            {/* TODO !!rate.promotions.length && <div></div> */}
            {rate.inclusions.length > 0 && <VerticalSpacer gap={4}>
              {rate.inclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
            </VerticalSpacer>}
          </div>
        </AccommodationPackageTileContentArea>
        <AccommodationPackageTilePricingArea>
          {(!rate.includesFlights || (typeof rate.cheapestFlightPrice === 'number' && rate.cheapestFlightPrice > 0)) && <Group direction="horizontal" gap={4}>
            {bedbankDiscount > 0 && <PriceRowValueDiscount
              type="estimate"
              discountPercentage={bedbankDiscount}
            />}
            {rate.canPayLater && <PayLaterPill />}
          </Group>}
          <AccommodationPackageTilePricingBlocks>
            <AccommodationPackageTilePricingBlock
              title={undefined}
              callToAction={ctas}
            >
              {rate.includesFlights && rate.cheapestFlightPrice === 'loading' && <LoadingIndicator floating opaque />}
              {(!rate.includesFlights || (typeof rate.cheapestFlightPrice === 'number' && rate.cheapestFlightPrice > 0)) && <VerticalSpacer gap={12}>
                <div>
                  <PriceRowPriceCaption>
                    {rate.includesFlights && <LinePlaneDepartureIcon size="S" />} {pluralizeToString('night', duration)}{rate.includesFlights && ' + flights'} from
                  </PriceRowPriceCaption>
                  <LuxPlusPriceGuard
                    showMemberPrice={(rate.luxPlusTotal?.displayPrice ?? 0) > 0}
                    fallbackPrice={<PriceRowPrice
                      size="M"
                      price={rate.total.displayPrice}
                      saleUnit="total"
                    />}
                  >
                    <LuxPlusPriceStack
                      size="M"
                      price={rate.total.displayPrice}
                      memberPrice={rate.luxPlusTotal?.displayPrice ?? 0}
                      saleUnit="total"
                    />
                  </LuxPlusPriceGuard>
                  <div ref={ref}></div>
                  <PriceRowTaxesAndFees />
                  {!!rawBedbankDiscount && <PriceRowValueDiscountWithCaption
                    size="M"
                    originalValue={rate.total.valuedUpToPrice}
                  />}
                  {/* NB: Don't pass `flightPrice` to `<OfferPriceDetailsRow/>`, as it's already baked in. */}
                  <OfferPriceDetailsRow
                    trigger="price-row"
                    triggerSize="M"
                    offerPage
                    offer={bedbankOffer}
                    rooms={rooms}
                    propertyFees={rate.total.propertyFees}
                    taxesAndFees={rate.total.taxesAndFees}
                    hotelPrice={rate.total.sellingPrice}
                    hotelMemberPrice={rate.luxPlusTotal?.sellingPrice}
                    showMemberPrice={(rate.luxPlusTotal?.displayPrice ?? 0) > 0}
                    duration={duration}
                    cta={<Group direction="vertical" tabletDirection="horizontal-reverse" tabletHorizontalAlign="end" gap={8}>
                      {ctas}
                    </Group>}
                  />
                </div>
                <LoyaltyPointsEarnWithBreakdownModal
                  calculationRequests={pointsEarnCalculationRequests}
                  calculationType={rate.includesFlights ? 'estimate' : 'exact'}
                />
              </VerticalSpacer>}
              {rate.includesFlights && (typeof rate.cheapestFlightPrice === 'string' || rate.cheapestFlightPrice === 0) && <VerticalSpacer gap={12}>
                {(rate.cheapestFlightPrice === 'originSameAsDestination' || rate.cheapestFlightPrice === 'unavailable') && <BodyText colour="neutral-two" variant="small">
                  No flights available from {originAirport?.name}. Please select another departing airport.
                </BodyText>}
                {rate.cheapestFlightPrice === 'departureTooEarly' && <BodyText colour="neutral-two" variant="small">
                  Flights cannot be booked online when check-in is within 24 hours.
                </BodyText>}
                {rate.cheapestFlightPrice === 'departureTooLate' && <BodyText colour="neutral-two" variant="small">
                  Flights can only be booked up to 11 months in advance.
                </BodyText>}
                {rate.cheapestFlightPrice === 0 && <BodyText colour="neutral-two" variant="small">
                  Add flights to the hotel package. Select from all major airlines at the next step.
                </BodyText>}
              </VerticalSpacer>}
            </AccommodationPackageTilePricingBlock>
          </AccommodationPackageTilePricingBlocks>
        </AccommodationPackageTilePricingArea>
      </AccommodationPackageTile>
    )
  }
}

export default withOfferPageAndReduxState((state, appState) => ({
  originAirport: state.airport,
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  duration: state.duration,
  rooms: state.rooms,
  regionCode: appState.geo.currentRegionCode,
  currency: appState.geo.currentCurrency,
  isLoggedIn: selectLoggedIn(appState),
  subscriber: getEnquirySubscriber(appState),
  open: !!state.activeFilter,
}), PropertyRateOption, preventRenderWithOpenFilter)
